<template>
  <div id="loyaltyAccount">
    <div class="container">

    <PageTitle :title="$t('loyalty_transactions.loyalty_account')"></PageTitle>

      <Tabs>
        <TabSection :title="$t('loyalty_accounts.adjustment')">
          <div class="columns">
            <div class="column">
              <div class="balances">
                <div class="group" v-if="hasBalance">
                  <label>{{$t('loyalty_accounts.balance')}}</label>
                  <div class="value">{{($tc(loyaltyAccount.balance, loyaltyAccount.currency))}}</div>
                </div>
                <div class="group" v-if="hasPoints">
                  <label>{{$t('loyalty_accounts.points')}}</label>
                  <div class="value">{{loyaltyAccount.points || '0'}}</div>
                </div>
              </div>
              <button class="btnRecharge btn btn-primary" @click="showAdjustmentForm = !showAdjustmentForm">{{$t('loyalty_accounts.add_funds')}}</button>
              <div class="recharge" v-show="showAdjustmentForm">
                <p>
                  <img src="@/assets/img/icon/info.png">
                  {{$t('loyalty_accounts.explanation')}}
                </p>
                <div class="group">
                  <label>{{$t('loyalty_accounts.explicative_note')}}</label>
                  <div class="field">
                    <select v-model="adjustment.moreData.reasonCode">
                      <option :value="null">{{$t('loyalty_transactions.reason_codes.null')}}</option>
                      <option :value="r.code" v-for="r of reasons">{{$t(`loyalty_transactions.reason_codes.${r.code}`)}}</option>
                    </select>
                    <input type="text" v-model="adjustment.moreData.note">
                  </div>
                </div>
                <div class="group">
                  <label>{{$t('loyalty_accounts.amount')}}</label><!-- TODO ADD BRANCH CURRENCY SIGN -->
                  <div class="field">
                    <select v-model="adjustment.select" v-if="hasPoints && hasBalance">
                      <option value="balance">{{$t('loyalty_accounts.balance')}}</option>
                      <option value="points">{{$t('loyalty_accounts.points')}}</option>
                    </select>
                    <input type="number" v-model="adjustment.amount" :placeholder="$t('loyalty_accounts.enter_amount_placeholder')"><!-- TODO validate this field (& format) -->
                    <button class="btn btn-primary" @click="applyAdjustment">{{$t('confirm')}}</button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </TabSection>

        <TabSection :title="$t('loyalty_accounts.details')">
          <div class="columns">
            <div class="column">
              <div class="group">
                <label>{{$t('loyalty_accounts.owner')}}</label>
                <div>{{loyaltyAccount.owner ? loyaltyAccount.owner.username : ''}}</div>
              </div>
              <div class="group">
                <label>{{$t('loyalty_accounts.description')}}</label>
                <input type="text" v-model="loyaltyAccount.description">
              </div>
            </div>
            <div class="column" v-if="eWalletActivated">
              <label>{{$t('loyalty_accounts.users')}}</label>
              <div class="user-box" v-for="(e, index) of loyaltyAccount.customers" :key="`u_${e.customer.user.id}`">
                <button class="btn btn-primary btnIcon" :class="{invisible: isOwner(e.customer.user.id)}" @click="removeUser(index)">
                  <svgicon src="/icon/close.svg" :height="18" :width="12"/>
                </button>
                <div style="flex-grow: 1">
                  <div>{{e.customer.user.username}}</div>
                  <div>{{fullName(e.customer.user)}}</div>
                </div>
                <div style="width: 200px">
                  <div v-for="p of allPermissions">
                    <input :id="`chk_${p}_${e.customer.user.id}`" type="checkbox" :value="p" v-model="e.permissions">
                    <label :for="`chk_${p}_${e.customer.user.id}`">{{$t(`loyalty_accounts.${p}`)}}</label>
                  </div>
                </div>
              </div>
              <div class="add-user-line">
                <button class="btn btn-primary btnIcon" @click="addUser">
                  <svgicon src="/icon/add.svg" :height="18" :width="12"/>
                </button>
                <AjaxSelect
                  v-model="userToAdd"
                  api="/api/entity/user/select"
                  :query="{branch: branchId}"
                  :map-fn="i => i.username"
                />
              </div>
            </div>
          </div>
          <button class="btnSave btn btn-primary" @click="save">{{$t('save')}}</button>
        </TabSection>

        <TabSection :title="$t('loyalty_accounts.transactions')">
          <Datatable
            v-if="loyaltyAccount.id"
            compact
            ref="loyaltyTransactions"
            entity="loyalty_transaction_account"
            :parameters="{branch: branchId, account: loyaltyAccount.id}"
            :onRowClick="showTransaction"
            :rowActions="[{text: 'datatable.display', method: showTransaction}]">
          </Datatable>
        </TabSection>
      </Tabs>

    </div>
  </div>
</template>

<script>
  import { mapActions } from "vuex";
  import InformationBlock from "@/components/tools/InformationBlock.vue";
  import AjaxSelect from "@/components/tools/AjaxSelect.vue";
  import Datatable from "@/components/Datatable.vue";
  import PageTitle from "@/components/PageTitle";

  export default {
    name: "LoyaltyAccount",

    components: { Datatable, InformationBlock, AjaxSelect, PageTitle },

    data() {
      return {
        loyaltyAccount: {
          id: null,
          description: null,
          owner: null,
          balance: 0,
          points: null,
          currency: null,
          customers: [],
        },
        userToAdd: {
          id: null
        },
        reasons: [
          {code: 'forgotten'},
          {code: 'long_wait'},
          {code: 'dissatisfaction'},
          {code: 'marketing_sponsored'},
          {code: 'marketing_promotion'},
        ],
        showAdjustmentForm: false,
        allPermissions: ['consume', 'manage'],
        eWalletActivated: false,
        adjustment: {
          select: 'balance', /* not in adjustment */
          amount: 0, /* not in adjustment */
          money: 0,
          points: 0,
          moreData: {
            note: '',
            reasonCode: null
          }
        }
      }
    },

    computed: {
      branchId() {
        return this.$route.query.branch
      },
      hasPoints() {
        return true
      },
      hasBalance() {
        return true
      }
    },

    async mounted() {
      this.$store.state.pageTitleTranslationKey = "loyalty_accounts.single";
      this.$store.state.pageIcon = "/icon/loyalty.svg";

      let id = this.$route.query.id;
      if (id) {
        let account = await this.getLoyaltyAccount(id);
        if (account) {
          let branch = await this.$store.getters.getContextBranch;
          this.eWalletActivated = branch.eWalletActivated;

          this.setAccountData(account);
        } else {
          toast(this.$t("error.no_server"), "error");
        }
      }
    },

    methods: {
      ...mapActions(['getLoyaltyAccount', 'putLoyaltyAccount', 'postLoyaltyAccountAdjustment']),

      fullName(obj) {
        return ((obj.firstName || '') + ' ' + (obj.lastName || '')).trim()
      },

      isOwner(id) {
        return id === this.loyaltyAccount.owner.id;
      },

      setAccountData(response) {
        for(let key of Object.keys(this.loyaltyAccount)) {
          if (response[key]) {
            this.loyaltyAccount[key] = response[key]
          }
        }
      },

      addUser() {
        if (!this.userToAdd.id) {
          toast(this.$t("loyalty_accounts.no_user"),"error");
          return;
        }
        if (this.loyaltyAccount.customers.find(e => e.customer.user.id === this.userToAdd.id)) {
          toast(this.$t("loyalty_accounts.user_exists"),"error");
          return;
        }
        this.loyaltyAccount.customers.push({
          customer: {
            information: null,
            user: this.userToAdd
          },
          permissions: []
        });
        this.userToAdd = { id: null };
      },

      removeUser(index) {
        this.loyaltyAccount.customers.splice(index, 1)
      },

      async save() {
        this.$store.commit("showSpinner", true);
        let response = await this.putLoyaltyAccount({
          id: this.loyaltyAccount.id,
          data: {
            description: this.loyaltyAccount.description,
            userToLoyaltyAccounts: this.eWalletActivated
              ? this.loyaltyAccount.customers.map(e => ({ user: e.customer.user.id, permissions: e.permissions }))
              : undefined
          }
        });
        this.$store.commit("showSpinner", false);

        if (response) {
          this.setAccountData(response);
          toast(this.$t("loyalty_accounts.save_success"),"success");
        } else {
          toast(this.$t("error.no_server"), "error");
        }
      },

      async applyAdjustment() {
        if (this.adjustment.money === 0 && this.adjustment.points === 0 && this.adjustment.amount === 0) {
          toast(this.$t('loyalty_accounts.errors.points_or_money_required'), 'error');
          return;
        }
        this.adjustment.moreData.note = this.adjustment.moreData.note.trim();
        if (!this.adjustment.moreData.note) {
          toast(this.$t('loyalty_accounts.errors.explicative_note_required'), 'error');
          return;
        }
        let data = {
          points: this.adjustment.select === 'points' ? parseFloat(this.adjustment.amount) : undefined,
          money: this.adjustment.select === 'balance' ? parseFloat(this.adjustment.amount) : undefined,
          moreData: this.adjustment.moreData
        };
        let transaction = await this.postLoyaltyAccountAdjustment({id: this.loyaltyAccount.id, data: data});
        if (transaction) {
          let toastMessages = [];
          if (transaction.points) {
            let ptsMessage = transaction.points >= 0
              ? this.$t("loyalty_accounts.adjustment_points_added")
              : this.$t("loyalty_accounts.adjustment_points_subtracted");

            ptsMessage = ptsMessage.replace('%POINTS%', transaction.points);
            toastMessages.push(ptsMessage)
          }
          if (transaction.money) {
            let moneyMessage = transaction.money >= 0
              ? this.$t("loyalty_accounts.adjustment_money_added")
              : this.$t("loyalty_accounts.adjustment_money_subtracted");

            moneyMessage = moneyMessage.replace('%MONEY%', this.$tc(transaction.money, transaction.currency));
            toastMessages.push(moneyMessage)
          }

          if (toastMessages.length) {
            toast(toastMessages.join('<br>'), "success");
          }
          this.adjustment.money = 0;
          this.adjustment.points = 0;
          this.adjustment.amount = 0;
          this.adjustment.moreData.note = '';
          this.adjustment.moreData.reasonCode = null;
          this.showAdjustmentForm = false;
          this.setAccountData(({
            balance: transaction.account.balance,
            points: transaction.account.points,
          }));
          this.$refs.loyaltyTransactions.query()
        } else {
          toast(this.$t("error.no_server"), "error");
        }
      },

      showTransaction(row) {
        this.$router.push(this.$router.buildContext("/loyalty-transaction", {id: row.id}));
      }
    }
  }
</script>

<style lang="scss" scoped>
  #loyaltyAccount {
    display: flex;
    flex-direction: column;
  }

  .value {
    font-size: 18px;
  }
  .btnRecharge {
    margin-top: 20px;
    margin-bottom: 15px;
  }
  .balances {
    display: flex;
    .group {
      margin-right: 20px;
    }
  }
  .field {
    display: flex;
    width: 100%;
    input {
      flex-grow: 1;
      margin-right: 5px;
    }
    select {
      margin-right: 5px;
    }
  }
  .btnIcon {
    display: flex;
    fill: white;
    margin-right: 5px;

    &.invisible {
      visibility: hidden;
    }
  }
  .user-box {
    display: flex;
    justify-content: space-between;
    margin-bottom: 20px;
  }
  .add-user-line {
    display: flex;
  }
</style>